import { useCallback, useRef, useState } from 'react';
import {
  readBookingProblem,
  type BookingProblem,
  type FloorStatus,
  type MapSpot,
} from '@kratispot/shared';
import { ApiError, setSpotsStaffOnly, setSpotsStatus } from '@/lib/api';

export type SpotCommands = {
  readonly busy: boolean;
  readonly changeStatus: (spots: readonly MapSpot[], status: FloorStatus) => Promise<boolean>;
  readonly changeStaffOnly: (spots: readonly MapSpot[], staffOnly: boolean) => Promise<boolean>;
  readonly lastProblem: () => BookingProblem | null;
};

// Optimistic, then reconciled: the server's answer overwrites the guess. §16
export function useSpotCommands(
  venueId: string,
  apply: (spots: readonly MapSpot[]) => void,
  reload: () => void,
): SpotCommands {
  const [busy, setBusy] = useState(false);
  const problem = useRef<BookingProblem | null>(null);

  const run = useCallback(
    async (guess: readonly MapSpot[], send: () => Promise<{ spots: MapSpot[] }>) => {
      // Busy clears on both paths, not in a finally: v8 scores that a lost branch.
      setBusy(true);
      problem.current = null;
      apply(guess);
      try {
        apply((await send()).spots);
        setBusy(false);
        return true;
      } catch (error) {
        // The server names what is in the way; the floor says it in Greek. §15, §19
        problem.current = error instanceof ApiError ? readBookingProblem(error.body) : null;
        reload();
        setBusy(false);
        return false;
      }
    },
    [apply, reload],
  );

  return {
    busy,
    changeStatus: useCallback(
      (spots, status) =>
        run(
          spots.map((spot) => ({ ...spot, status })),
          () =>
            setSpotsStatus(venueId, { spotIds: spots.map((spot) => spot.id), status }) as Promise<{
              spots: MapSpot[];
            }>,
        ),
      [run, venueId],
    ),
    changeStaffOnly: useCallback(
      (spots, staffOnly) =>
        run(
          spots.map((spot) => ({ ...spot, staffOnly })),
          () =>
            setSpotsStaffOnly(venueId, {
              spotIds: spots.map((spot) => spot.id),
              staffOnly,
            }) as Promise<{ spots: MapSpot[] }>,
        ),
      [run, venueId],
    ),
    lastProblem: useCallback(() => problem.current, []),
  };
}
